import React from 'react'
import { useParams } from 'react-router-dom'

import MasterStudioAIPage, { STUDIO_AI_TABS } from '../Master'
import ErrorMessage from '../../../components/errors/ErrorMessage'
import { INTERVIEW_TOPICS } from '../types'
import { useI18N } from '../../../../libraries/i18n/hook'
import { AppError } from '../../../../libraries/errors/AppError'
import { useJobOffer } from '../../../containers/job-offers/hooks'
import LoadingMessages from '../../../components/ai-interviewer/LoadingMessages'
import JobHeader from '../../../components/job-offers/JobHeader'
import { useDismissInterview, useInterviewForJobOffer, useStartInterview } from '../hooks'
import { getTopics } from '../index'
import AudioInterviewStatusRenderer from '../../../components/ai-interviewer/AudioInterviewStatusRenderer'

const JobOfferAiInterview = () => {
  const { id: jobOfferId, topic: selectedTopic } = useParams<{ id: string, topic: INTERVIEW_TOPICS }>()
  const { t } = useI18N()
  const { data: jobOffer, isLoading: isLoadingJobOffer, error: jobOfferError } = useJobOffer(jobOfferId ? parseInt(jobOfferId, 10) : null)
  const { data: currentInterview, isLoading: isLoadingCurrentInterview, error: currentInterviewError } = useInterviewForJobOffer(jobOfferId, selectedTopic)

  const { trigger: dismissInterview, isMutating: isDismissingInterview, error: errorDismissingInterview } = useDismissInterview()
  const { trigger: startInterview, isMutating: isStartingInterview, error: errorStartingInterview } = useStartInterview()

  const TOPICS = getTopics([
    { value: INTERVIEW_TOPICS.CUSTOM },
    { value: INTERVIEW_TOPICS.BEHAVIOURAL, numberOfFollowUps: 1 },
    { value: INTERVIEW_TOPICS.FIT },
  ])

  React.useEffect(() => {
    if(!isLoadingCurrentInterview && (!currentInterview || currentInterview.isDismissed)) {
      start()
    }
  }, [currentInterview])

  const start = async () => {
    if(!selectedTopic) return
    const selectedTopicData = TOPICS[selectedTopic]
    try {
      if(!selectedTopicData) { throw new AppError('Invalid topic selected') }
      await startInterview({
        topic: selectedTopicData.value,
        numberOfMainQuestions: selectedTopicData.numberOfQuestions as number,
        numberOfFollowUpQuestions: selectedTopicData.numberOfFollowUps as number,
        description: jobOffer?.description,
        jobOfferId,
      })
    } catch (error) {
      throw new AppError('Error starting interview')
    }
  }

  const onDismissInterview = () => {
    if(currentInterview) {
      dismissInterview({ sessionId: currentInterview.session_id })
    }
  }

  const isLoading = isStartingInterview || isLoadingCurrentInterview || isLoadingJobOffer || isDismissingInterview
  const apiError = jobOfferError || currentInterviewError || errorDismissingInterview || errorStartingInterview
  if(!selectedTopic) { return <ErrorMessage message='No topic selected' /> }
  const goBackLink = `/jobs/${jobOfferId}/application`
  return (
    <MasterStudioAIPage activeTab={STUDIO_AI_TABS.AI_INTERVIEW} buttons={null} goBackLink={goBackLink}>
      {apiError && <ErrorMessage message={apiError?.message} />}
      {isLoading && <LoadingMessages interviewStatus={currentInterview?.state} />}
      {!isLoading && !currentInterviewError && currentInterview && (
        <AudioInterviewStatusRenderer
          interviewStatus={currentInterview}
          evaluation={currentInterview?.feedback
            ? { decision: currentInterview.decision ?? false, feedback: currentInterview.feedback }
            : undefined
          }
          selectedTopic={selectedTopic}
          error={apiError?.message}
          onClearInterview={onDismissInterview}
          goBackLink={goBackLink}
          description={!isLoadingJobOffer && jobOffer && <JobHeader heading={t('studioAi.home.aiInterview.job.header')} jobOffer={jobOffer} size='sm' color='gray-300' />}
        />
      )}
    </MasterStudioAIPage>
  )
}

export default JobOfferAiInterview
